import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '@app/components/common/buttons/Button/Button';
import { ColumnsType } from 'antd/es/table';
import { Table } from '@app/components/common/Table/Table';
import { PageTitle, Toolbar, ToolbarActions } from '@app/components/common/PageToolbar/PageToolbar';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { doGetContent, setContent } from '@app/store/slices/dataSlice';
import { notificationController } from '@app/controllers/notificationController';
import { Image, Space, Input, Select, Tag } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Card as CommonCard } from '@app/components/common/Card/Card';
import { DayjsDatePicker } from '@app/components/common/pickers/DayjsDatePicker';
import { getProfilePhoto } from '@app/utils/utils';
import { ContentModel } from '@app/models/ContentModel';
import { useNavigate } from 'react-router-dom';
import { Checkbox } from '@app/components/common/Checkbox/Checkbox';
import { deleteContent } from '@app/api/admin.api';

const { RangePicker } = DayjsDatePicker;

const BRAND_COLORS: Record<string, string> = { ease: '#4A7556', wavon: '#D89070' };

const isScheduled = (record: ContentModel): boolean =>
  !!record.publish_date && record.publish_date > dayjs().format('YYYY-MM-DD');

const ContentPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const content = useAppSelector((state) => state.data.content);
  const [isLoading, setLoading] = useState(false);
  const [keyWord, setKeyWord] = useState('');
  const [brand, setBrand] = useState<string>('all');
  const [range, setRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  useEffect(() => {
    fetchContent();
  }, []);

  // Client-side filter by keyword (title/brand), brand, and created-date range.
  const filteredContentRequests = useMemo(() => {
    const kw = keyWord.trim().toLowerCase();
    const start = range && range[0] ? range[0].startOf('day').valueOf() : null;
    const end = range && range[1] ? range[1].endOf('day').valueOf() : null;
    return (content || []).filter((data: ContentModel) => {
      if (kw && !(data.title?.toLowerCase().includes(kw) || data.brand?.toLowerCase().includes(kw))) return false;
      if (brand !== 'all' && (data.brand || '').toLowerCase() !== brand) return false;
      if (start != null && end != null) {
        const c = dayjs(data.created_at).valueOf();
        if (c < start || c > end) return false;
      }
      return true;
    });
  }, [keyWord, brand, range, content]);

  const fetchContent = () => {
    setLoading(true);
    dispatch(doGetContent())
      .unwrap()
      .then((res) => {
        dispatch(setContent(res));
        setLoading(false);
      })
      .catch((err) => {
        notificationController.error({ message: err.message });
        setLoading(false);
      });
  };

  const columns: ColumnsType<ContentModel> = [
    {
      title: 'Media',
      dataIndex: 'media',
      width: 160,
      render: (text: string, record: ContentModel) => (
        <Image preview={false} height={120} src={getProfilePhoto(record.image)} />
      ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
    },
    {
      title: 'Brand',
      dataIndex: 'brand',
      sorter: (a, b) => (a.brand || '').localeCompare(b.brand || ''),
      render: (text: string) => (
        <Tag color={BRAND_COLORS[(text || '').toLowerCase()]} style={{ textTransform: 'capitalize' }}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'created_at',
      width: 140,
      defaultSortOrder: 'descend',
      sorter: (a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf(),
      render: (value: string) => (value ? dayjs(value).format('MMM D, YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 190,
      render: (text: string, record: ContentModel) =>
        isScheduled(record) ? (
          <Tag color="gold">Scheduled · {dayjs(record.publish_date as string).format('MMM D, YYYY')}</Tag>
        ) : (
          <Tag color="green">Published</Tag>
        ),
    },
    {
      title: 'Posted To',
      dataIndex: 'postedTo',
      width: '15%',
      render: (text: string, record: ContentModel) => {
        return (
          <Space direction="vertical">
            <Checkbox checked={record.available_in_about_screen} onClick={() => false} onChange={() => false}>
              About Us
            </Checkbox>
            <Checkbox checked={record.available_in_explore_screen} onClick={() => false} onChange={() => false}>
              Explore
            </Checkbox>
          </Space>
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      render: (text: string, record: ContentModel) => {
        return (
          <Space>
            <Button size="small" type="primary" icon={<DeleteOutlined />} onClick={() => {
              navigate(`edit`, { state: { record } });
            }}>
              {'Edit'}
            </Button>
            <Button danger size="small" type="primary" icon={<DeleteOutlined />} onClick={() => {
              deleteContent(record.id)
                .then((res) => {
                  console.log('res', res);
                  notificationController.success({ message: 'Content deleted successfully.' });
                  fetchContent()
                })
                .catch((error) => {
                  console.log('error: ', error);
                  notificationController.error({ message: error });
                });
            }}>
              {'Delete'}
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Toolbar>
        <PageTitle>Content</PageTitle>
        <ToolbarActions>
          <Input
            style={{ width: 260 }}
            allowClear
            placeholder={'Search by keyword'}
            value={keyWord}
            onChange={(e) => setKeyWord(e.target.value)}
          />
          <Select
            style={{ width: 130 }}
            value={brand}
            onChange={setBrand}
            options={[
              { label: 'All brands', value: 'all' },
              { label: 'Ease', value: 'ease' },
              { label: 'Wavon', value: 'wavon' },
            ]}
          />
          <RangePicker
            value={range as never}
            onChange={(v: unknown) => setRange((v as [dayjs.Dayjs, dayjs.Dayjs]) || null)}
            format="MMM D, YYYY"
          />
          <Button
            type="primary"
            onClick={() => {
              navigate('create');
            }}
          >
            {'Upload Content'}
          </Button>
        </ToolbarActions>
      </Toolbar>
      <CommonCard id="basic-table" padding="1.25rem 1.25rem 0">
        <Table
          columns={columns}
          dataSource={filteredContentRequests}
          loading={isLoading}
          scroll={{ x: 800 }}
        />
      </CommonCard>
    </>
  );
};

export default ContentPage;
